import { isRelation } from '@contember/schema-utils';
import { isDataGridScope, isDetailScope, isPluginScope } from '../helpers';
import { isElementFieldArgs, isSortElementDetailFieldArgs, isSortElementFormFieldArgs, isSortElementPreviewFieldArgs, } from '../types/helpers';
import { AppStructureBuilderError } from './error';
export function addForm({ formName, scopeName, viewName, entityName }) {
    this.updateAppStructure(draftBuilder => {
        draftBuilder.application.forms[formName] = this.getForm(formName) ?? this.newForm({ formName, entityName });
        if (scopeName && viewName) {
            const view = draftBuilder.getView(viewName);
            const scope = view?.scopes[scopeName];
            if (!view) {
                throw new AppStructureBuilderError(`View ${viewName} not found`);
            }
            if (!scope) {
                throw new AppStructureBuilderError(`Scope ${scopeName} not found in view ${viewName}`);
            }
            if (scope.entityName !== entityName) {
                throw new AppStructureBuilderError(`Scope ${scopeName} is for entity ${scope.entityName} and cannot have form ${formName} for entity ${entityName}. You have to create new form for this entity.`);
            }
            if (isDetailScope(scope)) {
                return this.addReadout({ readoutName: formName, entityName, viewName, scopeName });
            }
            if (isDataGridScope(scope)) {
                throw new AppStructureBuilderError(`Scope ${scopeName} is a data grid scope and cannot have forms for entity ${entityName}`);
            }
            if (isPluginScope(scope)) {
                throw new AppStructureBuilderError(`Scope ${scopeName} is a plugin scope and cannot have forms`);
            }
            scope.forms = scope.forms || [];
            if (!scope.forms.includes(formName)) {
                scope.forms.push(formName);
            }
        }
    });
    return this;
}
export function removeForm(formName) {
    this.updateAppStructure(draftBuilder => {
        const views = Object.values(draftBuilder.application.views);
        for (const view of views) {
            for (const scope of Object.values(view.scopes)) {
                if (isPluginScope(scope) || isDataGridScope(scope) || isDetailScope(scope)) {
                    continue;
                }
                if (scope.forms?.includes(formName)) {
                    scope.forms = scope.forms.filter(it => it !== formName);
                }
            }
        }
        delete draftBuilder.application.forms[formName];
    });
    return this;
}
export function removeFormFromScope({ formName, viewName, scopeName }) {
    this.updateAppStructure(draftBuilder => {
        const view = draftBuilder.getView(viewName);
        const scope = view?.scopes[scopeName];
        if (!view) {
            throw new AppStructureBuilderError(`View ${viewName} not found`);
        }
        if (!scope) {
            throw new AppStructureBuilderError(`Scope ${scopeName} not found in view ${viewName}`);
        }
        if (isPluginScope(scope) || isDataGridScope(scope) || isDetailScope(scope)) {
            throw new AppStructureBuilderError(`Scope ${scopeName} cannot have forms`);
        }
        if (scope.forms?.includes(formName)) {
            scope.forms = scope.forms.filter(it => it !== formName);
        }
    });
    return this;
}
export function addCreateForm({ createFormName, formName, fieldName }) {
    this.updateAppStructure(draftBuilder => {
        const form = draftBuilder.getForm(formName);
        if (!form) {
            throw new AppStructureBuilderError(`Form ${formName} not found`);
        }
        const field = form.fields[fieldName];
        if (!field) {
            throw new AppStructureBuilderError(`Field ${fieldName} not found in form ${formName}`);
        }
        // TODO: better check
        if (!['select', 'multiselect', 'repeater'].includes(field.type)) {
            throw new AppStructureBuilderError(`Field ${fieldName} is not a select, multiselect, or repeater`);
        }
        ;
        field.createNewForm = createFormName;
    });
    return this;
}
export function removeCreateForm({ formName, fieldName }) {
    this.updateAppStructure(draftBuilder => {
        const form = draftBuilder.getForm(formName);
        if (!form) {
            throw new AppStructureBuilderError(`Form ${formName} not found`);
        }
        const field = form.fields[fieldName];
        if (!field) {
            throw new AppStructureBuilderError(`Field ${fieldName} not found in form ${formName}`);
        }
        // TODO: better check
        if (!['select', 'multiselect', 'repeater'].includes(field.type)) {
            throw new AppStructureBuilderError(`Field ${fieldName} is not a select, multiselect, or repeater`);
        }
        ;
        field.createNewForm = undefined;
    });
    return this;
}
export function addFormField({ formName, fieldName, fieldType, plugin, pluginParams }) {
    this.updateAppStructure(draftBuilder => {
        const form = draftBuilder.getForm(formName);
        if (!form) {
            throw new AppStructureBuilderError(`Form ${formName} not found. You must add the form before adding fields.`);
        }
        const entityName = draftBuilder.getFormEntityName(formName);
        if (!entityName) {
            throw new AppStructureBuilderError(`Entity name not found for form ${formName}. You must add the form to a scope before adding fields.`);
        }
        const modelField = draftBuilder.getEntityField(entityName, fieldName);
        const field = form.fields[fieldName];
        const numberOfFields = field ? field.order : Object.keys(form.fields).length;
        const type = this.isPluginRelation({ fieldName, entityName }) ? 'plugin' : draftBuilder.getFieldType({ fieldType, modelField });
        if (type !== 'plugin' && isRelation(modelField) && !['select', 'multiselect', 'repeater'].includes(type)) {
            const relationTypeMap = {
                OneHasOne: 'select',
                OneHasMany: 'multiselect',
                ManyHasOne: 'select',
                ManyHasMany: 'multiselect',
            };
            throw new AppStructureBuilderError(`Field ${fieldName} is a relation, but it is not a relation field. You should use ${relationTypeMap[modelField.type]} type.`);
        }
        form.fields[fieldName] = this.newElementField({
            fieldName,
            fieldType: type,
            modelField,
            plugin,
            numberOfFields,
            pluginParams,
        });
    });
    return this;
}
export function removeElement(args) {
    this.updateAppStructure(draftBuilder => {
        if (isElementFieldArgs(args)) {
            const { formName, fieldName } = args;
            const form = draftBuilder.getForm(formName);
            if (!form) {
                throw new AppStructureBuilderError(`Form ${formName} not found`);
            }
            if (!form.fields[fieldName]) {
                throw new AppStructureBuilderError(`Element ${fieldName} not found in form ${formName}`);
            }
            delete form.fields[fieldName];
            return;
        }
        const { viewName, scopeName, columnName } = args;
        if (viewName && scopeName) {
            const { scope } = draftBuilder.getScopeByView({ scopeName, viewName });
            if (!scope) {
                throw new AppStructureBuilderError(`Scope ${scopeName} not found in view ${viewName}`);
            }
            if (isPluginScope(scope)) {
                throw new AppStructureBuilderError(`Scope ${scopeName} is a plugin scope and cannot have elements`);
            }
            if (isDataGridScope(scope) && scope.columns) {
                if (!scope.columns[columnName]) {
                    throw new AppStructureBuilderError(`Element ${columnName} not found in scope ${scopeName}`);
                }
                delete scope.columns[columnName];
                return;
            }
        }
    });
    return this;
}
export function sortElements(args) {
    this.updateAppStructure(draftBuilder => {
        if (isSortElementFormFieldArgs(args)) {
            const { formName, fieldNames } = args;
            const form = draftBuilder.getForm(formName);
            fieldNames.forEach((fieldName, index) => {
                const field = form.fields[fieldName];
                if (!field) {
                    throw new AppStructureBuilderError(`Field ${fieldName} not found in form ${formName}.`);
                }
                field.order = index;
            });
            return;
        }
        if (isSortElementDetailFieldArgs(args)) {
            const { readoutName, fieldNames } = args;
            const readout = draftBuilder.getReadout(readoutName);
            fieldNames.forEach((fieldName, index) => {
                const field = readout.fields[fieldName];
                if (!field) {
                    throw new AppStructureBuilderError(`Field ${fieldName} not found in readout ${readoutName}.`);
                }
                field.order = index;
            });
            return;
        }
        if (isSortElementPreviewFieldArgs(args)) {
            const { previewName, fieldNames } = args;
            const preview = draftBuilder.getPreview(previewName);
            fieldNames.forEach((fieldName, index) => {
                const field = preview.fields[fieldName];
                if (!field) {
                    throw new AppStructureBuilderError(`Field ${fieldName} not found in preview ${previewName}.`);
                }
                field.order = index;
            });
            return;
        }
        const { viewName, scopeName, columnNames } = args;
        const { scope } = draftBuilder.getScopeByView({ scopeName, viewName });
        if (scope && isDataGridScope(scope)) {
            columnNames.forEach((columnName, index) => {
                const column = scope.columns ? scope.columns[columnName] : undefined;
                if (!column) {
                    throw new AppStructureBuilderError(`Column ${columnName} not found in scope ${scopeName}.`);
                }
                column.order = index;
            });
        }
    });
    return this;
}
//# sourceMappingURL=formManagement.js.map